import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type Theme = "system" | "light" | "dark";

const KEY = "bsmeet.theme";

const HOST_THEME_MESSAGE = "bsmeet:theme";

type ThemeApi = { theme: Theme; setTheme: (theme: Theme) => void };

const ThemeContext = createContext<ThemeApi | null>(null);

function asTheme(value: unknown): Theme | null {
  return value === "light" || value === "dark" || value === "system" ? value : null;
}

function fromLink(): Theme | null {
  try {
    return asTheme(new URLSearchParams(globalThis.location.search).get("theme"));
  } catch {
    return null;
  }
}

function load(): Theme {
  const asked = fromLink();
  if (asked !== null) return asked;
  try {
    const stored = globalThis.localStorage.getItem(KEY);
    return stored === "light" || stored === "dark" ? stored : "system";
  } catch {
    return "system";
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(load);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "system") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", theme);
  }, [theme]);

  useEffect(() => {
    if (globalThis.parent === globalThis.self) return;
    const onMessage = (event: MessageEvent) => {
      if (event.source !== globalThis.parent) return;
      const data: unknown = event.data;
      if (typeof data !== "object" || data === null) return;
      const frame = data as { source?: unknown; theme?: unknown };
      if (frame.source !== HOST_THEME_MESSAGE) return;
      const next = asTheme(frame.theme);
      if (next !== null) setThemeState(next);
    };
    globalThis.addEventListener("message", onMessage);
    return () => {
      globalThis.removeEventListener("message", onMessage);
    };
  }, []);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      if (next === "system") globalThis.localStorage.removeItem(KEY);
      else globalThis.localStorage.setItem(KEY, next);
    } catch (error) {
      console.error("theme preference save failed:", error);
    }
  }, []);

  const api = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);
  return <ThemeContext value={api}>{children}</ThemeContext>;
}

export function useTheme(): ThemeApi {
  const api = use(ThemeContext);
  if (api === null) throw new Error("useTheme used outside ThemeProvider");
  return api;
}
